import uuidv1 from 'uuid/v1';
import { getComponentSetting } from './component';

const MAX_HYDRAULICS = 5;
const CONTROLBOX_HEIGHT = 184;
const MAX_CONTROLS = 5;
const WIRE_SPACE = 10; // Distance between red & blue wire

const SOLAR_COLLECTOR_TYPES = {
  ON_ROOF: {
    name: 'on_roof',
    connectionIn: {
      wire1Offset: { x: 10, y: WIRE_SPACE * 0.5 },
      wire2Offset: { x: 20, y: -WIRE_SPACE * 0.5 },
      offset: {
        x: 100,
        y: 100,
      },
    },
  },
  IN_ROOF: {
    name: 'in_roof',
    connectionIn: {
      wire1Offset: { x: 0, y: WIRE_SPACE * 0.5 },
      wire2Offset: { x: 0, y: -WIRE_SPACE * 0.5 },
      offset: {
        x: 120,
        y: 100,
      },
    },
  },
  HORIZONTAL_ROOF: {
    name: 'horizontal',
    connectionIn: {
      wire1Offset: { x: -WIRE_SPACE * 0.5, y: 0 },
      wire2Offset: { x: WIRE_SPACE * 0.5, y: 0 },
    },
    align: {
      x: 0.5,
      y: 1,
    },
  },
};

const circuitComponentInfo = {
  direct: {
    editable: false,
    removable: false,
    selectable: false,

    width: 70,
    height: 70,
  },
  mixed: {
    editable: false,
    removable: false,
    selectable: false,

    width: 74,
    height: 74,
  },
};
// Alias for mixed circuit
circuitComponentInfo.gemengd = circuitComponentInfo.mixed;

const controlboxComponentInfo = {
  width: 130,
  height: CONTROLBOX_HEIGHT,
};

const doubleControlboxComponentInfo = {
  width: controlboxComponentInfo.width * 2,
  height: CONTROLBOX_HEIGHT,
};

const configuratorComponentInfo = {
  slot: {
    control: {
      // default info of a config component
      def: {
        editable: true,
        removable: true,
        width: 80,
        height: 50,
        align: { x: 0.5, y: 0.5 },
      },
      ids: {
        // Custom info of a config component per ID
      },
    },
    hydraulics: {
      def: controlboxComponentInfo,
    },
    external_unit: {
      def: {
        selectable: false,
        width: 205,
        height: 210,
        align: { x: 0.5, y: 0.5 }, // Alignment when on a wall
        floorAlign: { x: 0.4, y: 0.9 }, // Alignment when placed on floor
        onFloor: true, // default placed on floor
      },
    },
    external_pump: {
      def: {
        selectable: true,
        width: 205,
        height: 210,
        align: { x: 0.5, y: 0.5 }, // Alignment when on a wall
        floorAlign: { x: 0.4, y: 0.9 }, // Alignment when placed on floor
        onFloor: true, // default placed on floor
      },
    },
    external_unit_2: {
      def: {
        width: 205,
        height: 210,
        align: { x: 0.5, y: 0.5 },
        floorAlign: { x: 0.4, y: 0.9 },
        onFloor: true,
      },
    },
    kettle: {
      def: {
        width: 200,
        height: 330,
        align: { x: 0.5, y: 0.4 },
        floorAlign: { x: 0.5, y: 0.9 },
        onFloor: false,
      },
      ids: {
        1: null,
      },
    },
    pump: {
      def: {
        width: 216,
        height: 380,
        align: { x: 0.5, y: 0.5 },
        floorAlign: { x: 0.4, y: 0.9 },
        onFloor: true,
      },
    },
    exhaust: {
      def: {
        editable: false,

        width: 50,
        height: 450,
        align: { x: 0.5, y: 0.8 },
      },
    },
    'solar-kit': {
      def: {
        removable: false,

        width: 225,
        height: 225,
        align: { x: 0, y: 1 },
      },
    },
    boiler: {
      def: {
        width: 180,
        height: 512,
        floorAlign: { x: 0.5, y: 0.9 },
        align: { x: 0.5, y: 0.7 },
        onFloor: false,
      },
    },
  },
};

const aliases = [
  ['external_unit', 'external_unit-1', 'external_unit_1'],
  ['external_unit-2', 'external_unit_2'],
  ['boiler', 'solar_boiler'],
];

// Assign same component info for all controls
for (let i = 0; i < MAX_CONTROLS; i += 1) {
  const index = i + 1;
  configuratorComponentInfo.slot[`controls-${index}`] = configuratorComponentInfo.slot.control;
  configuratorComponentInfo.slot[`controls_${index}`] = configuratorComponentInfo.slot.control;

  aliases.push([
    `controls-${index}`,
    `controls_${index}`,
    `control-${index}`,
    `control_${index}`,
  ]);
}

// Assign same component info for all hydraulics
for (let i = 0; i < MAX_HYDRAULICS; i += 1) {
  const index = i + 1;
  configuratorComponentInfo.slot[`hydraulics-${index}`] = configuratorComponentInfo.slot.hydraulics;
  configuratorComponentInfo.slot[`hydraulics_${index}`] = configuratorComponentInfo.slot.hydraulics;
}

const aliasMap = {};
{
  const num = aliases.length;
  for (let i = 0; i < num; i += 1) {
    const valuesArr = aliases[i];
    const numValues = valuesArr ? valuesArr.length : 0;
    for (let j = 0; j < numValues; j += 1) {
      aliasMap[valuesArr[j]] = valuesArr;
    }
  }
}

export function getNextItemId(prefix = '') {
  const id = uuidv1();
  if (!prefix) {
    return id;
  }
  return `${prefix}_${id}`;
}

export function getComponentInfo(type, id) {
  if (typeof (type) !== 'string') {
    return null;
  }
  const data = configuratorComponentInfo.slot[type];
  if (!data) {
    return null;
  }
  let result = null;
  const { ids } = data;
  if (id && ids) {
    result = ids[id];
  }
  if (result) {
    return result;
  }
  // Return default value
  return data.def;
}

export function getComponentInfoOfObject(object) {
  if (!object) {
    return null;
  }
  return object.displayInfo;
}

export function isObjectOnFloor(object) {
  if (!object) {
    return false;
  }
  const { onFloor } = object;
  if (typeof (onFloor) === 'boolean') {
    return onFloor;
  }
  const type = getComponentInfoOfObject(object);
  if (!type) {
    return false;
  }
  return !!type.onFloor;
}

// TODO: Duplicate of canRemoveProductFromConfig (project-definition-data-access
// Should be removed later I think?
export function isObjectRemovable(object) {
  const defaultResult = true;
  if (!object) {
    return defaultResult;
  }
  const { displayInfo, productInfo } = object;
  if (productInfo) {
    return !productInfo.required;
  }
  if (!displayInfo) {
    return defaultResult;
  }
  const { removable } = displayInfo;
  if (typeof (removable) === 'boolean') {
    return removable;
  }
  return defaultResult;
}

export function isObjectEditable(object) {
  if (!object) {
    return false;
  }
  const defaultResult = true;
  const { displayInfo } = object;
  if (!displayInfo) {
    return defaultResult;
  }
  const { editable } = displayInfo;
  if (typeof (editable) === 'boolean') {
    return editable;
  }
  return defaultResult;
}

export function isObjectSelectable(object) {
  if (!object) {
    return false;
  }

  const setting = getComponentSetting(object.name, 'selectable', null);
  if (typeof (setting) === 'boolean') {
    return setting;
  }

  const defaultResult = true;
  const { displayInfo } = object;
  if (!displayInfo) {
    return defaultResult;
  }
  const { selectable } = displayInfo;
  if (typeof (selectable) === 'boolean') {
    return selectable;
  }
  return defaultResult;
}

export {
  SOLAR_COLLECTOR_TYPES,
  MAX_HYDRAULICS,
  CONTROLBOX_HEIGHT,
  MAX_CONTROLS,
  WIRE_SPACE,
  configuratorComponentInfo,
  circuitComponentInfo,

  controlboxComponentInfo,
  doubleControlboxComponentInfo,

  aliasMap,
};



// WEBPACK FOOTER //
// ./js/utils/project-definition/config-item-info.js